<template>
<div class="contents">
  <div class="list-box">
    <div class="err-msg-area" v-if="errorMessage">
      <span class="font_orange">{{ errorMessage }}</span>
    </div>

    <table class="tbl">
      <colgroup>
        <col style="width: 5%;">
        <col style="width: 8%;">
        <col style="width: 20%;">
        <col style="width: 20%;">
        <col style="width: 10%;">
        <col style="width: 12%;">
        <col style="width: 12%;">
        <col style="width: 13%;">
      </colgroup>
      <tr>
        <th class="tbl-cell tbl-header-cell"></th>
        <th class="tbl-cell tbl-header-cell">車両ID</th>
        <th class="tbl-cell tbl-header-cell">車両名</th>
        <th class="tbl-cell tbl-header-cell">登録番号</th>
        <th class="tbl-cell tbl-header-cell">積載量</th>
        <th class="tbl-cell tbl-header-cell">所有者</th>
        <th class="tbl-cell tbl-header-cell">時間単価</th>
        <th class="tbl-cell tbl-header-cell">距離単価</th>
      </tr>
      <tr v-for="vehicle in vehicleRows" :key="vehicle.id">
        <td class="tbl-cell checkbox-cell">
          <input
            type="checkbox"
            :checked="selectedVehicleIds.includes(vehicle.id)"
            @change="toggleVehicleSelection(vehicle.id)"
          />
        </td>
        <td class="tbl-cell">{{ vehicle.id }}</td>
        <td class="tbl-cell">{{ vehicle.name }}</td>
        <td class="tbl-cell">{{ vehicle.registrationNumber }}</td>
        <td class="tbl-cell">{{ vehicle.capacity }}</td>
        <td class="tbl-cell">{{ vehicle.owner }}</td>
        <td class="tbl-cell">{{ vehicle.costPerHour }}</td>
        <td class="tbl-cell">{{ vehicle.costPerKilometer }}</td>
      </tr>
    </table>
  </div>

  <div class="vehicle-action-buttons">
    <button class="submit1 bg_green" @click="openCreatePage">
      新規登録
    </button>
    <button class="submit1 bg_blue" @click="openEditPage">
      編集
    </button>
    <button class="submit1 bg_red" @click="deleteVehicles">
      削除
    </button>
  </div>
</div>
</template>

<script>
import { AdminPageType } from '@/consts/admin'
import adminVehicleApi from '@/api/adminVehicle'
import { getApiErrorMessage } from '@/utils/apiErrorHelper'
import { useAdminPageAccess } from '@/composables/useAdminPageAccess'

export default {
  name: 'admin-vehicles',
  setup(props) {
    const { ensureManagerAccess } = useAdminPageAccess(props.adminType)
    return { ensureManagerAccess }
  },
  props: {
    adminType: {
      type: String,
      default: AdminPageType.ADMIN
    }
  },
  data() {
    return {
      errorMessage: '',
      selectedVehicleIds: [],
      vehicleRows: [],
    }
  },
  async mounted() {
    try {
      const canAccessPage = await this.ensureManagerAccess()
      if (!canAccessPage) return

      await this.fetchVehicles()
    } catch (error) {
      this.errorMessage = '車両情報画面の初期化に失敗しました。'
    }
  },
  methods: {
    async fetchVehicles() {
      try {
        const { data: vehicleRowsResponse } = await adminVehicleApi.index()
        this.vehicleRows = vehicleRowsResponse.map(vehicle => ({
          id: vehicle.id,
          name: vehicle.vehicle_name,
          registrationNumber: vehicle.registration_number,
          capacity: vehicle.payload_capacity,
          owner: vehicle.owner_name,
          costPerHour: vehicle.cost_per_hour,
          costPerKilometer: vehicle.cost_per_kilometer,
        }))
      } catch (error) {
        this.errorMessage = '車両情報の取得に失敗しました。'
      }
    },
    toggleVehicleSelection(vehicleId) {
      this.errorMessage = ''
      if (this.selectedVehicleIds.includes(vehicleId)) {
        this.selectedVehicleIds = this.selectedVehicleIds.filter(id => id !== vehicleId)
        return
      }
      this.selectedVehicleIds = [...this.selectedVehicleIds, vehicleId]
    },
    openCreatePage() {
      this.errorMessage = ''
      const routeName = this.adminType === AdminPageType.STAFF ? 'staff-vehicles-create' : 'admin-vehicles-create'
      this.$router.push({ name: routeName })
    },
    openEditPage() {
      if (this.selectedVehicleIds.length !== 1) {
        this.errorMessage = '編集する場合は車両を1件だけ選択してください。'
        return
      }

      this.errorMessage = ''
      const routeName = this.adminType === AdminPageType.STAFF ? 'staff-vehicles-edit' : 'admin-vehicles-edit'
      this.$router.push({
        name: routeName,
        params: { id: String(this.selectedVehicleIds[0]) }
      })
    },
    async deleteVehicles() {
      if (this.selectedVehicleIds.length === 0) {
        this.errorMessage = '削除する車両を選択してください。'
        return
      }

      const shouldDelete = confirm('選択した車両情報を削除します。よろしいですか？')
      if (!shouldDelete) {
        return
      }

      this.errorMessage = ''
      try {
        const deleteResults = await Promise.allSettled(
          this.selectedVehicleIds.map(vehicleId => adminVehicleApi.delete(vehicleId))
        )
        const failures = deleteResults.filter(result => result.status === 'rejected')
        this.selectedVehicleIds = []
        await this.fetchVehicles()

        if (failures.length === 1) {
          this.errorMessage = getApiErrorMessage(failures[0].reason, '車両情報の削除に失敗しました。')
        } else if (failures.length > 1) {
          this.errorMessage = `車両情報の削除に一部失敗しました。（失敗:${failures.length}件）`
        }
      } catch (error) {
        this.errorMessage = '車両情報の削除に失敗しました。'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.contents {
  width: 95%;
  margin: 20px auto 0;
  min-height: 400px;
}

.err-msg-area {
  margin-bottom: 10px;
}

.list-box {
  width: 960px;
  max-width: 100%;

  .tbl {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 13px;
    background-color: #ffffff;
  }

  .tbl-cell {
    border: 1px solid #b8c0cc;
    padding: 4px 6px;
  }

  .tbl-header-cell {
    text-align: left;
    background-color: #dce7f8;
    font-weight: 700;
  }
}

.checkbox-cell {
  text-align: center;
}

.vehicle-action-buttons {
  display: flex;
  gap: 16px;
  margin-top: 18px;

  .submit1 {
    width: 190px;
  }
}
</style>
